import {
  CANDIDATE_STATUS_LABELS_INTERNAL,
  COMPANY_STATUS_LABELS,
  CONSENT_LABELS,
  CURATOR_DECISION_LABELS,
  CURATOR_STATUS_LABELS,
  ROLE_STATUS_LABELS,
  RUN_STATUS_LABELS,
  type CandidateStatus,
  type COMPANY_STATUSES,
  type CONSENT_STATES,
  type CURATOR_DECISIONS,
  type CuratorStatus,
  type RoleStatus,
  type RunStatus,
} from '@remote-medium/shared';
import { Badge } from '@remote-medium/ui';
import { Check, Clock, LogOut, Pause, X, type LucideIcon } from 'lucide-react';

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'destructive';
interface Look {
  variant: BadgeVariant;
  icon?: LucideIcon;
}
type CompanyStatus = (typeof COMPANY_STATUSES)[number];
type Decision = (typeof CURATOR_DECISIONS)[number];
type Consent = (typeof CONSENT_STATES)[number] | 'awaiting';

// One status language across the curator app: warning = waiting on someone,
// success = good or done, outline = neutral or finished, destructive = failed
// or blocked. Icons repeat the meaning so colour is never the only signal.
const CANDIDATE: Record<CandidateStatus, Look> = {
  new: { variant: 'warning' },
  screened: { variant: 'secondary', icon: Check },
  active: { variant: 'success', icon: Check },
  paused: { variant: 'outline', icon: Pause },
  placed: { variant: 'outline' },
  withdrawn: { variant: 'outline', icon: LogOut },
};

const ROLE: Record<RoleStatus, Look> = {
  draft: { variant: 'outline' },
  open: { variant: 'outline' },
  matching: { variant: 'secondary' },
  shortlisted: { variant: 'warning', icon: Clock },
  introduced: { variant: 'default', icon: Check },
  closed: { variant: 'outline', icon: X },
};

const COMPANY: Record<CompanyStatus, Look> = {
  pending: { variant: 'warning' },
  active: { variant: 'success', icon: Check },
  suspended: { variant: 'destructive' },
};

const DECISION: Record<Decision, Look> = {
  pending: { variant: 'warning', icon: Clock },
  confirmed: { variant: 'success', icon: Check },
  rejected: { variant: 'outline', icon: X },
};

const CONSENT: Record<Consent, Look> = {
  pending: { variant: 'outline' },
  // Asked (match.consent_requested_at set), no answer yet.
  awaiting: { variant: 'warning', icon: Clock },
  yes: { variant: 'success', icon: Check },
  no: { variant: 'outline', icon: X },
};

// job_run.status ('running' | 'ok' | 'failed') and email_log.status
// ('sent' | 'logged' | 'failed').
const RUN: Record<RunStatus, Look> = {
  running: { variant: 'secondary', icon: Clock },
  ok: { variant: 'success', icon: Check },
  sent: { variant: 'success', icon: Check },
  logged: { variant: 'outline' },
  failed: { variant: 'destructive' },
};

const CURATOR: Record<CuratorStatus, Look> = {
  active: { variant: 'success', icon: Check },
  invited: { variant: 'warning', icon: Clock },
  expired: { variant: 'destructive' },
  deactivated: { variant: 'outline', icon: X },
};

function StatusChip({ look, text }: { look: Look; text: string }) {
  const Icon = look.icon;
  return (
    <Badge variant={look.variant} className="rounded-sm">
      {text}
      {Icon && <Icon data-icon="inline-end" />}
    </Badge>
  );
}

function chip<K extends string>(map: Record<K, Look>, value: K, text: string) {
  return <StatusChip look={map[value]} text={text} />;
}

export const CandidateStatusChip = ({ status }: { status: CandidateStatus }) =>
  chip(CANDIDATE, status, CANDIDATE_STATUS_LABELS_INTERNAL[status]);
export const RoleStatusChip = ({ status }: { status: RoleStatus }) =>
  chip(ROLE, status, ROLE_STATUS_LABELS[status]);
export const CompanyStatusChip = ({ status }: { status: CompanyStatus }) =>
  chip(COMPANY, status, COMPANY_STATUS_LABELS[status]);
export const DecisionChip = ({ status }: { status: Decision }) =>
  chip(DECISION, status, CURATOR_DECISION_LABELS[status]);
export const ConsentChip = ({ status }: { status: Consent }) =>
  chip(CONSENT, status, CONSENT_LABELS[status]);
export const CuratorStatusChip = ({ status }: { status: CuratorStatus }) =>
  chip(CURATOR, status, CURATOR_STATUS_LABELS[status]);
// These two columns are free-text varchar in the DB, so values arrive as
// strings; anything unrecognised renders as a neutral chip with the raw value.
function looseChip<K extends string>(
  map: Record<K, Look>,
  labels: Record<K, string>,
  value: string,
) {
  const known = value in map;
  return known ? (
    chip(map, value as K, labels[value as K])
  ) : (
    <StatusChip look={{ variant: 'outline' }} text={value} />
  );
}

export const RunStatusChip = ({ status }: { status: string }) =>
  looseChip<RunStatus>(RUN, RUN_STATUS_LABELS, status);
